import React, { useState, useEffect, useRef } from 'react';
import { api } from '../utils/api';
import './Admin.css';

// ── Rich Editor ───────────────────────────────────────────────────────────────
function RichEditor({ value, onChange }) {
  const editorRef = useRef(null);
  const exec = (cmd, val = null) => { editorRef.current.focus(); document.execCommand(cmd, false, val); syncContent(); };
  const syncContent = () => { if (editorRef.current) onChange(editorRef.current.innerHTML); };
  useEffect(() => { if (editorRef.current && editorRef.current.innerHTML !== value) editorRef.current.innerHTML = value || ''; }, []); // eslint-disable-line

  const handlePaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData('text/html');
    const text = e.clipboardData.getData('text/plain');
    if (html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      doc.querySelectorAll('span[style]').forEach(span => {
        const s = span.getAttribute('style') || '';
        if (s.includes('bold') || s.includes('700')) { const b = document.createElement('strong'); b.innerHTML = span.innerHTML; span.replaceWith(b); }
        else if (s.includes('italic')) { const em = document.createElement('em'); em.innerHTML = span.innerHTML; span.replaceWith(em); }
        else span.replaceWith(...span.childNodes);
      });
      doc.querySelectorAll('*').forEach(el => { el.removeAttribute('class'); el.removeAttribute('id'); });
      document.execCommand('insertHTML', false, doc.body.innerHTML);
    } else {
      document.execCommand('insertHTML', false, text.split('\n').filter(l=>l.trim()).map(l=>`<p>${l}</p>`).join(''));
    }
    syncContent();
  };

  const TEXT_COLORS = ['#ffffff','#00d55b','#ff4444','#ffc107','#3498db','#9b59b6','#e67e22','#1abc9c','#aaaaaa','#555555','#e74c3c','#f39c12'];
  const BG_COLORS   = ['transparent','rgba(0,213,91,0.15)','rgba(255,68,68,0.15)','rgba(255,193,7,0.15)','rgba(52,152,219,0.15)','#1a1a1a'];
  const FONT_SIZES  = ['12px','14px','16px','18px','20px','24px','28px','32px','36px','48px'];

  return (
    <div className="rich-editor">
      <div className="rich-editor__toolbar rich-editor__toolbar--row">
        <div className="rich-tool-group">
          {[['B','bold','rich-bold'],['I','italic','rich-italic'],['U','underline','rich-underline'],['S̶','strikeThrough','rich-strike']].map(([l,c,cls])=>(
            <button key={l} type="button" className={`rich-tool-btn ${cls}`} onMouseDown={e=>{e.preventDefault();exec(c);}}>{l}</button>
          ))}
        </div>
        <div className="rich-tool-divider"/>
        <div className="rich-tool-group">
          {[['H1','<h1>'],['H2','<h2>'],['H3','<h3>'],['P','<p>']].map(([l,v])=>(
            <button key={l} type="button" className="rich-tool-btn" onMouseDown={e=>{e.preventDefault();exec('formatBlock',v);}}>{l}</button>
          ))}
        </div>
        <div className="rich-tool-divider"/>
        <div className="rich-tool-group">
          <button type="button" className="rich-tool-btn" onMouseDown={e=>{e.preventDefault();exec('insertUnorderedList');}}>• List</button>
          <button type="button" className="rich-tool-btn" onMouseDown={e=>{e.preventDefault();exec('insertOrderedList');}}>1. List</button>
          <button type="button" className="rich-tool-btn" onMouseDown={e=>{e.preventDefault();exec('formatBlock','<blockquote>');}}>❝ Quote</button>
        </div>
        <div className="rich-tool-divider"/>
        <div className="rich-tool-group">
          {[['⬡L','justifyLeft'],['⬡C','justifyCenter'],['⬡R','justifyRight']].map(([l,c])=>(
            <button key={l} type="button" className="rich-tool-btn" onMouseDown={e=>{e.preventDefault();exec(c);}}>{l}</button>
          ))}
        </div>
      </div>
      <div className="rich-editor__toolbar rich-editor__toolbar--row rich-editor__toolbar--row2">
        <div className="rich-tool-group rich-tool-group--colors">
          <span className="rich-tool-label">Text</span>
          {TEXT_COLORS.map(c=>(
            <button key={c} type="button" className="rich-color-btn" style={{background:c,border:c==='#ffffff'?'1px solid #333':'none'}} onMouseDown={e=>{e.preventDefault();exec('foreColor',c);}}/>
          ))}
        </div>
        <div className="rich-tool-divider"/>
        <div className="rich-tool-group rich-tool-group--colors">
          <span className="rich-tool-label">BG</span>
          {BG_COLORS.map(c=>(
            <button key={c} type="button" className="rich-color-btn" style={{background:c==='transparent'?'repeating-linear-gradient(45deg,#333 0,#333 2px,transparent 2px,transparent 6px)':c,border:'1px solid #333'}} onMouseDown={e=>{e.preventDefault();exec('hiliteColor',c);}}/>
          ))}
        </div>
        <div className="rich-tool-divider"/>
        <div className="rich-tool-group">
          <span className="rich-tool-label">Size</span>
          <select className="rich-select" defaultValue="" onChange={e=>{exec('insertHTML',`<span style="font-size:${e.target.value}">​</span>`);}}>
            <option value="" disabled>Size</option>
            {FONT_SIZES.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="rich-tool-divider"/>
        <div className="rich-tool-group">
          <button type="button" className="rich-tool-btn" onMouseDown={e=>{e.preventDefault();const u=prompt('URL:');if(u)exec('createLink',u);}}>🔗 Link</button>
          <button type="button" className="rich-tool-btn" onMouseDown={e=>{e.preventDefault();exec('removeFormat');}}>✕ Clear</button>
        </div>
      </div>
      <div className="rich-paste-tip">💡 Paste from Google Docs or Word — formatting cleaned automatically</div>
      <div ref={editorRef} className="rich-editor__body" contentEditable suppressContentEditableWarning onInput={syncContent} onBlur={syncContent} onPaste={handlePaste}/>
    </div>
  );
}

// ── Slug helper ───────────────────────────────────────────────────────────────
function toSlug(str) {
  return str.toLowerCase().replace(/[^a-z0-9\s-]/g,'').replace(/\s+/g,'-').replace(/-+/g,'-').trim();
}

const ADMIN_PASSWORD = 'msdiscoveries2025';

// ── Main Admin ────────────────────────────────────────────────────────────────
export default function Admin() {
  const [auth, setAuth]           = useState(() => sessionStorage.getItem('ms_admin_auth') === '1');
  const [pwInput, setPwInput]     = useState('');
  const [pwErr, setPwErr]         = useState('');
  const [view, setView]           = useState('dashboard');
  const [posts, setPosts]         = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [editing, setEditing]     = useState(null);
  const [toast, setToast]         = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedSub, setSelectedSub]     = useState(null);
  const [searchQ, setSearchQ]     = useState('');
  const [loadingPosts, setLoadingPosts]   = useState(false);
  const [loadingSubs, setLoadingSubs]     = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  // Load posts from API
  const loadPosts = () => {
    setLoadingPosts(true);
    api.adminGetPosts()
      .then(data => { setPosts(data.posts || []); setLoadingPosts(false); })
      .catch(() => { showToast('Failed to load posts'); setLoadingPosts(false); });
  };

  // Load submissions from API
  const loadSubmissions = () => {
    setLoadingSubs(true);
    api.adminGetSubmissions()
      .then(data => { setSubmissions(data.submissions || []); setLoadingSubs(false); })
      .catch(() => { setLoadingSubs(false); });
  };

  useEffect(() => {
    if (auth) { loadPosts(); loadSubmissions(); }
  }, [auth]); // eslint-disable-line

  const handleLogin = (e) => {
    e.preventDefault();
    if (pwInput === ADMIN_PASSWORD) { sessionStorage.setItem('ms_admin_auth','1'); setAuth(true); }
    else setPwErr('Incorrect password. Try again.');
  };
  const handleLogout = () => { sessionStorage.removeItem('ms_admin_auth'); setAuth(false); };

  const markRead = (id) => {
    api.adminMarkRead(id).then(() => {
      setSubmissions(s => s.map(x => x.id === id ? { ...x, is_read: 1 } : x));
    });
  };
  const deleteSubmission = (id) => {
    api.adminDeleteSubmission(id).then(() => {
      setSubmissions(s => s.filter(x => x.id !== id));
      setSelectedSub(null);
      showToast('Submission deleted.');
    });
  };

  const unreadCount = submissions.filter(s => !s.is_read).length;

  // ── LOGIN ──────────────────────────────────────────────────────────────────
  if (!auth) return (
    <div className="admin-login">
      <div className="admin-login__card">
        <div className="admin-login__logo"><span className="admin-logo-dot"/>MS Discoveries</div>
        <h2>Admin Panel</h2>
        <p>Enter your password to continue</p>
        <form onSubmit={handleLogin}>
          <div className="admin-field">
            <label>Password</label>
            <input type="password" value={pwInput} onChange={e=>{setPwInput(e.target.value);setPwErr('');}} placeholder="Enter admin password" autoFocus/>
            {pwErr && <span className="admin-error">{pwErr}</span>}
          </div>
          <button type="submit" className="admin-btn-primary">Sign In</button>
        </form>
      </div>
    </div>
  );

  // ── POST EDITOR ────────────────────────────────────────────────────────────
  if (view === 'create' || view === 'edit') {
    return <PostEditor
      post={editing}
      onSave={async (p) => {
        try {
          if (p.id) await api.adminUpdatePost(p.id, p);
          else await api.adminCreatePost(p);
          showToast(p.id ? 'Post updated!' : 'Post published!');
          loadPosts();
          setView('list');
        } catch { showToast('Failed to save post'); }
      }}
      onCancel={() => setView('list')}
    />;
  }

  const published = posts.filter(p => p.published).length;
  const drafts    = posts.filter(p => !p.published).length;
  const filtered  = posts.filter(p => p.title.toLowerCase().includes(searchQ.toLowerCase()));
  const fmt = d => { try { return new Date(d).toLocaleString(); } catch { return d; } };

  return (
    <div className="admin-shell">
      {toast && <div className="admin-toast">{toast}</div>}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="admin-overlay">
          <div className="admin-modal">
            <h3>Delete Post</h3>
            <p>Delete "<strong>{deleteConfirm.title}</strong>"? This cannot be undone.</p>
            <div className="admin-modal__btns">
              <button className="admin-btn-danger" onClick={() => {
                api.adminDeletePost(deleteConfirm.id).then(() => { loadPosts(); showToast('Post deleted.'); setDeleteConfirm(null); });
              }}>Delete</button>
              <button className="admin-btn-ghost" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Submission detail */}
      {selectedSub && (
        <div className="admin-overlay" onClick={() => setSelectedSub(null)}>
          <div className="admin-modal admin-modal--wide" onClick={e => e.stopPropagation()}>
            <h3>Contact Submission</h3>
            <div className="sub-detail">
              <div className="sub-detail__row"><strong>Name</strong><span>{selectedSub.name}</span></div>
              <div className="sub-detail__row"><strong>Email</strong><a href={`mailto:${selectedSub.email}`}>{selectedSub.email}</a></div>
              {selectedSub.phone && <div className="sub-detail__row"><strong>Phone</strong><a href={`tel:${selectedSub.phone}`}>{selectedSub.phone}</a></div>}
              {selectedSub.business && <div className="sub-detail__row"><strong>Business</strong><span>{selectedSub.business}</span></div>}
              {selectedSub.product && <div className="sub-detail__row"><strong>Interested In</strong><span>{selectedSub.product}</span></div>}
              <div className="sub-detail__row"><strong>Date</strong><span>{fmt(selectedSub.submitted_at)}</span></div>
              <div className="sub-detail__message"><strong>Message</strong><p>{selectedSub.message}</p></div>
            </div>
            <div className="admin-modal__btns">
              <a href={`mailto:${selectedSub.email}`} className="admin-btn-primary">Reply via Email</a>
              <button className="admin-btn-ghost" onClick={() => setSelectedSub(null)}>Close</button>
              <button className="admin-btn-danger" onClick={() => deleteSubmission(selectedSub.id)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar__brand"><span className="admin-logo-dot"/>MS Admin</div>
        <nav className="admin-nav">
          <button className={view==='dashboard'?'active':''} onClick={()=>setView('dashboard')}><span>📊</span> Dashboard</button>
          <button className={view==='submissions'?'active':''} onClick={()=>setView('submissions')}>
            <span>📬</span> Submissions {unreadCount > 0 && <span className="admin-badge">{unreadCount}</span>}
          </button>
          <button className={view==='list'?'active':''} onClick={()=>setView('list')}><span>📝</span> All Posts</button>
          <button onClick={()=>{setEditing(null);setView('create');}}><span>✏️</span> New Post</button>
          <a href="/" target="_blank" rel="noreferrer"><span>🌐</span> View Site</a>
          <a href="/blog" target="_blank" rel="noreferrer"><span>📰</span> View Blog</a>
        </nav>
        <button className="admin-logout" onClick={handleLogout}><span>🔓</span> Logout</button>
      </aside>

      {/* Main */}
      <main className="admin-main">
        {/* DASHBOARD */}
        {view === 'dashboard' && (
          <div className="admin-dashboard">
            <div className="admin-page-header">
              <h1>Dashboard</h1>
              <button className="admin-btn-primary" onClick={()=>{setEditing(null);setView('create');}}>+ New Post</button>
            </div>
            <div className="admin-stats">
              <div className="admin-stat-card admin-stat-card--green" style={{cursor:'pointer'}} onClick={()=>setView('submissions')}>
                <div className="admin-stat-card__val">{unreadCount}</div>
                <div className="admin-stat-card__label">Unread Enquiries</div>
              </div>
              <div className="admin-stat-card"><div className="admin-stat-card__val">{submissions.length}</div><div className="admin-stat-card__label">Total Submissions</div></div>
              <div className="admin-stat-card"><div className="admin-stat-card__val">{posts.length}</div><div className="admin-stat-card__label">Total Posts</div></div>
              <div className="admin-stat-card"><div className="admin-stat-card__val">{published}</div><div className="admin-stat-card__label">Published</div></div>
              <div className="admin-stat-card"><div className="admin-stat-card__val">{drafts}</div><div className="admin-stat-card__label">Drafts</div></div>
            </div>

            {submissions.length > 0 && (
              <div className="admin-recent">
                <div className="admin-section-header"><h2>Recent Enquiries</h2><button className="admin-btn-ghost" onClick={()=>setView('submissions')}>View All →</button></div>
                <div className="admin-post-list">
                  {submissions.slice(0,5).map(s=>(
                    <div key={s.id} className={`admin-post-row ${!s.is_read?'admin-post-row--unread':''}`} style={{cursor:'pointer'}} onClick={()=>{markRead(s.id);setSelectedSub(s);}}>
                      <div className="admin-post-row__info">
                        {!s.is_read && <span className="admin-unread-dot"/>}
                        <strong>{s.name}</strong>
                        <span className="admin-cat-tag">{s.email}</span>
                        {s.product && <span className="admin-cat-tag">{s.product}</span>}
                        <span className="admin-post-date">{new Date(s.submitted_at).toLocaleDateString()}</span>
                      </div>
                      <span className="admin-arrow-btn">→</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="admin-recent" style={{marginTop:'32px'}}>
              <div className="admin-section-header"><h2>Recent Posts</h2><button className="admin-btn-ghost" onClick={()=>setView('list')}>View All →</button></div>
              {loadingPosts ? <div className="admin-empty"><p>Loading...</p></div> : posts.length === 0 ? (
                <div className="admin-empty"><p>No posts yet. <button className="admin-link" onClick={()=>{setEditing(null);setView('create');}}>Create your first post →</button></p></div>
              ) : (
                <div className="admin-post-list">
                  {posts.slice(0,5).map(p=>(
                    <div key={p.id} className="admin-post-row">
                      <div className="admin-post-row__info">
                        <span className={`admin-status ${p.published?'published':'draft'}`}>{p.published?'Published':'Draft'}</span>
                        <strong>{p.title}</strong>
                        <span className="admin-post-date">{new Date(p.createdAt).toLocaleDateString()}</span>
                      </div>
                      <div className="admin-post-row__actions">
                        <button className="admin-icon-btn" onClick={()=>{setEditing(p);setView('edit');}}>✏️</button>
                        <button className="admin-icon-btn danger" onClick={()=>setDeleteConfirm(p)}>🗑️</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* SUBMISSIONS */}
        {view === 'submissions' && (
          <div className="admin-list-view">
            <div className="admin-page-header"><h1>Contact Submissions <span style={{fontSize:'16px',color:'var(--green)'}}>({submissions.length})</span></h1>
              <button className="admin-btn-ghost" onClick={loadSubmissions}>↻ Refresh</button>
            </div>
            {loadingSubs ? <div className="admin-empty"><p>Loading...</p></div> : submissions.length === 0 ? (
              <div className="admin-empty"><p>No submissions yet.</p></div>
            ) : (
              <div className="admin-post-list">
                {submissions.map(s=>(
                  <div key={s.id} className={`admin-post-row ${!s.is_read?'admin-post-row--unread':''}`} style={{cursor:'pointer'}} onClick={()=>{markRead(s.id);setSelectedSub(s);}}>
                    <div className="admin-post-row__info">
                      {!s.is_read && <span className="admin-unread-dot"/>}
                      <strong>{s.name}</strong>
                      <span className="admin-cat-tag">{s.email}</span>
                      {s.phone && <span className="admin-cat-tag">{s.phone}</span>}
                      {s.product && <span className="admin-cat-tag" style={{background:'rgba(0,213,91,0.1)',color:'var(--green)',border:'1px solid rgba(0,213,91,0.2)'}}>{s.product}</span>}
                      <span className="admin-post-date">{new Date(s.submitted_at).toLocaleString()}</span>
                    </div>
                    <div className="admin-post-row__actions">
                      <a href={`mailto:${s.email}`} className="admin-icon-btn" onClick={e=>e.stopPropagation()} title="Reply">📧</a>
                      <button className="admin-icon-btn danger" onClick={e=>{e.stopPropagation();deleteSubmission(s.id);}} title="Delete">🗑️</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ALL POSTS */}
        {view === 'list' && (
          <div className="admin-list-view">
            <div className="admin-page-header">
              <h1>All Posts</h1>
              <div style={{display:'flex',gap:'10px'}}>
                <button className="admin-btn-ghost" onClick={loadPosts}>↻ Refresh</button>
                <button className="admin-btn-primary" onClick={()=>{setEditing(null);setView('create');}}>+ New Post</button>
              </div>
            </div>
            <div className="admin-search-bar">
              <input type="text" placeholder="Search posts..." value={searchQ} onChange={e=>setSearchQ(e.target.value)}/>
            </div>
            {loadingPosts ? <div className="admin-empty"><p>Loading...</p></div> : filtered.length === 0 ? (
              <div className="admin-empty"><p>No posts found.</p></div>
            ) : (
              <div className="admin-post-list">
                {filtered.map(p=>(
                  <div key={p.id} className="admin-post-row">
                    <div className="admin-post-row__info">
                      <span className={`admin-status ${p.published?'published':'draft'}`}>{p.published?'Published':'Draft'}</span>
                      <strong>{p.title}</strong>
                      {p.category && <span className="admin-cat-tag">{p.category}</span>}
                      <span className="admin-post-date">{new Date(p.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="admin-post-row__actions">
                      <button className="admin-icon-btn" onClick={()=>{setEditing(p);setView('edit');}}>✏️</button>
                      <button className="admin-icon-btn danger" onClick={()=>setDeleteConfirm(p)}>🗑️</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

// ── Post Editor ───────────────────────────────────────────────────────────────
function PostEditor({ post, onSave, onCancel }) {
  const isNew = !post;
  const [form, setForm] = useState({
    id: post?.id||'', title: post?.title||'', slug: post?.slug||'',
    excerpt: post?.excerpt||'', content: post?.content||'',
    category: post?.category||'', author: post?.author||'MS Discoveries',
    readTime: post?.readTime||post?.read_time||'', published: post?.published??false,
  });
  const [errors, setErrors]     = useState({});
  const [slugManual, setSlugManual] = useState(!!post);
  const [saving, setSaving]     = useState(false);

  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const handleTitle = v => { set('title',v); if (!slugManual) set('slug', toSlug(v)); };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Title required';
    if (!form.slug.trim())  e.slug  = 'Slug required';
    if (!form.excerpt.trim()) e.excerpt = 'Excerpt required';
    if (!form.content.trim() || form.content === '<br>') e.content = 'Content required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (publish) => {
    if (!validate()) return;
    setSaving(true);
    await onSave({ ...form, published: publish });
    setSaving(false);
  };

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-sidebar__brand"><span className="admin-logo-dot"/>MS Admin</div>
        <nav className="admin-nav"><button onClick={onCancel}><span>←</span> Back to Posts</button></nav>
      </aside>
      <main className="admin-main admin-editor-main">
        <div className="admin-page-header">
          <h1>{isNew ? 'New Post' : 'Edit Post'}</h1>
          <div className="admin-editor-actions">
            <button className="admin-btn-ghost" onClick={()=>handleSubmit(false)} disabled={saving}>💾 Save Draft</button>
            <button className="admin-btn-primary" onClick={()=>handleSubmit(true)} disabled={saving}>🚀 {saving ? 'Saving...' : isNew ? 'Publish' : 'Update'}</button>
            <button className="admin-btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          </div>
        </div>
        <div className="admin-editor-grid">
          <div className="admin-editor-left">
            <div className="admin-field">
              <label>Post Title *</label>
              <input type="text" value={form.title} onChange={e=>handleTitle(e.target.value)} placeholder="Enter post title..." className={errors.title?'error':''}/>
              {errors.title && <span className="admin-error">{errors.title}</span>}
            </div>
            <div className="admin-field">
              <label>Excerpt / Summary *</label>
              <textarea value={form.excerpt} onChange={e=>set('excerpt',e.target.value)} placeholder="Short summary shown on blog listing..." rows={3} className={errors.excerpt?'error':''}/>
              {errors.excerpt && <span className="admin-error">{errors.excerpt}</span>}
            </div>
            <div className="admin-field">
              <label>Content *</label>
              {errors.content && <span className="admin-error">{errors.content}</span>}
              <RichEditor value={form.content} onChange={v=>set('content',v)}/>
            </div>
          </div>
          <div className="admin-editor-right">
            <div className="admin-field">
              <label>URL Slug *</label>
              <input type="text" value={form.slug} onChange={e=>{setSlugManual(true);set('slug',toSlug(e.target.value));}} placeholder="post-url-slug" className={errors.slug?'error':''}/>
              {errors.slug && <span className="admin-error">{errors.slug}</span>}
              <small className="admin-hint">msdiscoveries.com/blog/{form.slug||'your-slug'}</small>
            </div>
            <div className="admin-field">
              <label>Category</label>
              <input type="text" value={form.category} onChange={e=>set('category',e.target.value)} placeholder="e.g. SEO, Email Marketing"/>
            </div>
            <div className="admin-field">
              <label>Author</label>
              <input type="text" value={form.author} onChange={e=>set('author',e.target.value)} placeholder="MS Discoveries"/>
            </div>
            <div className="admin-field">
              <label>Read Time (minutes)</label>
              <input type="number" value={form.readTime} onChange={e=>set('readTime',e.target.value)} placeholder="5" min="1"/>
            </div>
            <div className="admin-field">
              <label>Status</label>
              <div className="admin-toggle-row">
                <span>{form.published ? '🟢 Published' : '🟡 Draft'}</span>
                <label className="admin-toggle">
                  <input type="checkbox" checked={form.published} onChange={e=>set('published',e.target.checked)}/>
                  <span className="admin-toggle__slider"/>
                </label>
              </div>
            </div>
            <div className="admin-editor-tip">
              <strong>Tips</strong>
              <ul>
                <li>Use H2/H3 to structure content</li>
                <li>Add a clear excerpt for blog listing</li>
                <li>Category helps readers filter</li>
                <li>Slug auto-generates from title</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}